import { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import __ from "src/utils/Translations";
import { useGlobalModal } from "src/utils/GlobalModal/GlobalModalContext";

interface UseUnsavedChangesGuardParams {
  isDirty: boolean;
  title?: string;
  description?: string;
  continueLabel?: string;
  exitLabel?: string;
}

/**
 * Blocks route navigation when `isDirty` is true and shows a confirmation modal.
 * Uses `history.block` from react-router-dom v5.
 */
const useUnsavedChangesGuard = ({
  isDirty,
  title = __("Czy na pewno chcesz opuścić formularz?") as string,
  description = __(
    "Opuszczenie formularza spowoduje utratę wszystkich niezapisanych danych. Czy na pewno chcesz kontynuować?"
  ) as string,
  continueLabel = __("Kontynuuj edytowanie") as string,
  exitLabel = __("Wyjdź") as string,
}: UseUnsavedChangesGuardParams) => {
  const history = useHistory();
  const { setModal, resetModal } = useGlobalModal();
  const unblockRef = useRef<(() => void) | null>(null);

  const confirmLeaving = useCallback(
    (onConfirm: () => void) => {
      setModal({
        open: true,
        title,
        description,
        maxWidth: 500,
        disableOutsideClick: true,
        actions: [
          {
            label: continueLabel,
            variant: "outlined" as const,
            callbackFn: () => resetModal(),
          },
          {
            label: exitLabel,
            variant: "contained" as const,
            color: "error" as const,
            callbackFn: () => {
              onConfirm();
              resetModal();
            },
          },
        ],
      });
    },
    [setModal, resetModal, title, description, continueLabel, exitLabel]
  );

  useEffect(() => {
    if (!isDirty) {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      return undefined;
    }

    unblockRef.current = history.block((tx) => {
      confirmLeaving(() => {
        if (unblockRef.current) {
          unblockRef.current();
          unblockRef.current = null;
        }
        history.push(tx.pathname + tx.search + tx.hash);
      });

      return false;
    });

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [isDirty, history, confirmLeaving]);
};

export default useUnsavedChangesGuard;
